import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Image as ImageIcon, User, Globe, Twitch, Save } from "lucide-react";
import { Page, Status } from "../pages/Pages";
import TiptapEditor from "./TiptapEditor";
import { StaffMember } from "../pages/Staff";

type StaffFormProps = {
    onSubmit: (data: any) => void;
    staff?: StaffMember;

};

export default function StaffForm({ onSubmit, staff }: StaffFormProps) {
    const isNew = staff === null || staff === undefined;

    const [form, setForm] = useState({
        name: staff?.name ?? "",
        title: staff?.title ?? "",
        twitter: staff?.twitter ?? "",
        twitch: staff?.twitch ?? "",
        instagram: staff?.instagram ?? "",
        type: staff?.type ?? 0,
        order: staff?.order ?? 0,
        image: null as File | null,
        imagePreview: staff?.image ?? "",
    });

    function updateField(key: string, value: string | number) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        onSubmit(form);
    }

    const inputClass =
        "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100";

    const labelClass = "mb-2 block text-sm font-medium text-gray-700";

    return (
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-5 sm:px-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">
                                {isNew ? "Create Staff Member" : "Edit Staff Member"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Manage staff member information, social links, and other details.
                            </p>
                        </div>

                        <Link
                            href="/admin/staff"
                            className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            Cancel
                        </Link>
                    </div>
                </div>

                <div className="grid gap-8 px-6 py-6 sm:px-8 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-8">
                        <section className="space-y-5">
                            <div className="flex items-center gap-2">
                                <User size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Basic info</h3>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Name</label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => updateField("name", e.target.value)}
                                        className={inputClass}
                                        placeholder="Name"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Title</label>
                                    <input
                                        type="text"
                                        value={form.title}
                                        onChange={(e) => updateField("title", e.target.value)}
                                        className={inputClass}
                                        placeholder="Title"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Twitch</label>
                                    <input
                                        type="text"
                                        value={form.twitch}
                                        onChange={(e) => updateField("twitch", e.target.value)}
                                        className={inputClass}
                                        placeholder="Twitch"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Instagram</label>
                                    <input
                                        type="text"
                                        value={form.instagram}
                                        onChange={(e) => updateField("instagram", e.target.value)}
                                        className={inputClass}
                                        placeholder="Instagram"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Twitter</label>
                                    <input
                                        type="text"
                                        value={form.twitter}
                                        onChange={(e) => updateField("twitter", e.target.value)}
                                        className={inputClass}
                                        placeholder="Twitter"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Order</label>
                                    <input
                                        type="text"
                                        value={form.order}
                                        onChange={(e) => updateField("order", e.target.value)}
                                        className={inputClass}
                                        placeholder="Order"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Type</label>
                                    <select className={inputClass} value={form.type} onChange={(e) => updateField("type", e.target.value)}>
                                        <option value={0}>Staff</option>
                                        <option value={1}>Creator</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        <section className="space-y-5 border-t border-gray-100 pt-8">
                            <div className="flex items-center gap-2">
                                <Twitch size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Media</h3>
                            </div>

                            <div>
                                <label className={labelClass}>Image</label>
                                <input
                                    type="file"
                                    accept="image/png, image/jpeg, image/webp"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] ?? null;

                                        setForm((prev) => ({
                                            ...prev,
                                            image: file,
                                            imagePreview: file ? URL.createObjectURL(file) : prev.imagePreview,
                                        }));
                                    }}
                                    className={inputClass}
                                />
                                <p className="mt-2 text-sm text-gray-500">
                                    JPG, PNG, or WEBP only
                                </p>
                            </div>
                        </section>
                    </div>

                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
                    <Link
                        href="/admin/staff"
                        className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        Cancel
                    </Link>

                    <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    >
                        <Save size={16} />
                        {isNew ? "Create Staff Member" : "Save Changes"}
                    </button>
                </div>
            </div>
        </form>
    );
}